import { API_PATHS } from "../constants/api-paths";
import { COMMON_SSO_MOBILE } from "../constants/sso";
import { decryptLocalStorageMobileNo } from "@repo/shared-utils/crypto";
import { encryptedRequest, request } from "../core/api.client";

export interface AllStock {
  symbol: string;
  fullName: string;
  ltp: number;
  changePrice: string;
  perChange: string;
  exchange: string | number;
  token: number;
  stockStatus: string;
  redirectURL: string;
  logoURL: string;
}
export interface AllETFsStock {
  symbol: string;
  fullName: string;
  bseToken: number;
  nseToken: number;
  ltp: number;
  changePrice: string;
  perChange: string;
  exchange: string | number;
  token: number;
  stockStatus: string;
  redirectURL: string;
  logoURL: string;
}
export interface Stock {
  symbol: string;
  compName: string;
  exchId: number;
  token: number;
  ltp: number;
  openPrice: number;
  closePrice: number;
  prevClose: number;
  chg: string | number;
  chgPer: string | number;
  redirectURL: string;
  stockStatus: string;
}

export interface MarketIndices {
  fullName: string;
  symbol: string;
  ltp: number;
  exchange: string;
  token: number;
  openPrice: number;
  closePrice: number;
  highPrice: number;
  lowPrice: number;
  prevClose: number;
  changePrice: string;
  perChange: string;
  redirectURL: string;
  stockStatus: string;
}

export interface MarketMoversData {
  symbol: string;
  compName: string;
  exchId: number;
  token: number;
  ltp: number;
  prevClose: number;
  openPrice: number;
  closePrice: number;
  chg: string;
  chgPer: string;
  volume: number;
  coCode: number;
}
export interface GlobalIndices {
  exc: string;
  cmp: number;
  country: string;
  changePrice: string;
  perChange: string;
}

export interface SectorStock {
  symbol: string;
  compName: string;
  exchId: number;
  token: number;
  ltp: number;
  openPrice: number;
  closePrice: number;
  prevClose: number;
  chg: string;
  chgPer: string;
  redirectURL: string;
  stockStatus: string;
}
type ApiAllETFsStockResponseData = {
  etfList: AllETFsStock[];
  showMore: boolean;
  showLess: boolean;
};
export interface IdxDetails {
  compName: string;
  symbol: string;
  price: number;
  chg: string;
  chgPer: string;
  ltt: string;
  stockStatus: string;
}

type ApiAllStockResponseData = {
  stockList: AllStock[];
  showMore: boolean;
};
export interface MarketMoversResponseData {
  marketMovers: MarketMoversData[];
  showMore: boolean;
}
export interface IndicesData {
  indicesList: MarketIndices[];
  showMore: boolean;
}

export interface PiiIndicesData {
  indicesList: MarketIndices[];
}

export interface GlobalIndicesData {
  indicesList: GlobalIndices[];
  showMore: boolean;
}

export interface SectorStocksData {
  idxDetails: IdxDetails;
  stockList: SectorStock[];
  showMore: boolean;
}

export interface StockFilters {
  index: string[];
  sector: string[];
  mktcap: string[];
  alphabetic: string[];
}

export const fetchAllMarketsTodayStockApi = async (
  entity: string,
  limit?: number,
  page?: number
) => {
  const response = await request<IndicesData>({
    method: "POST",
    url: API_PATHS.fetchAllMarketsTodayStock,
    data: {
      entity,
      page: page || 1,
      limit: limit ?? 25,
    },
  });
  return response?.data;
};

export const fetchPIIIndicesData = async (): Promise<PiiIndicesData> => {
  try {
    const localStorageMobileNo = localStorage.getItem(COMMON_SSO_MOBILE);
    const mobileNo = await decryptLocalStorageMobileNo(localStorageMobileNo || "");
    if (!mobileNo) {
      throw new Error("Missing Mobile No");
    }
    const response = await encryptedRequest<PiiIndicesData>({
      method: "POST",
      url: API_PATHS.piiIndicesData,
      data: {
        mobileNo,
      },
    });
    return response?.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const savePIIIndicesData = async (payLoad: string[]) => {
  try {
    const localStorageMobileNo = localStorage.getItem(COMMON_SSO_MOBILE);
    const mobileNo = await decryptLocalStorageMobileNo(localStorageMobileNo || "");
    if (!mobileNo) {
      throw new Error("Missing Mobile No");
    }
    const response = await encryptedRequest({
      method: "POST",
      url: API_PATHS.savePIIIndicesData,
      data: {
        mobileNo,
        indices: payLoad,
      },
    });
    return response;
  } catch (error) {
    console.error(error);
    return null;
  }
};

export const fetchAllStockApi = async (
  entity: string,
  page?: number,
  limit?: number,
  sortBy?: string,
  filters?: StockFilters
) => {
  const response = await request<ApiAllStockResponseData>({
    method: "POST",
    url: API_PATHS.fetchAllStocks,
    data: {
      entity,
      exchId: 0,
      page: page || 1,
      limit: limit ?? 10,
      sort: sortBy ?? "",
      filters: filters ?? {
        index: [],
        sector: [],
        mktcap: [],
        alphabetic: [],
      },
    },
  });
  return response?.data;
};
export const fetchAllETFsApi = async (
  page?: number,
  limit?: number,
  sortBy?: string,
  filters?: StockFilters
) => {
  const response = await request<ApiAllETFsStockResponseData>({
    method: "POST",
    url: API_PATHS.fetchAllETFs,
    data: {
      exchId: 0,
      page: page ?? 1,
      limit: limit ?? 10,
      sort: sortBy ?? "",
      filters: filters ?? {
        index: [],
        sector: [],
        mktcap: [],
        alphabetic: [],
      },
    },
  });
  return response?.data;
};
export const fetchMarketMovers = async (
  entity: string,
  token?: number,
  page?: number,
  limit?: number,
  sortBy?: string,
  exhId?: number
) => {
  const response = await request<MarketMoversResponseData>({
    method: "POST",
    url: API_PATHS.marketMovers,
    data: {
      page: page ?? 1,
      limit: limit ?? 10,
      exchId: exhId ?? 0,
      token: token ?? 26000,
      orderBy: entity,
      sort: sortBy ?? "asec",
    },
  });
  return response?.data;
};

export const fetchGlobalIndices = async (page: number, limit?: number) => {
  const response = await request<GlobalIndicesData>({
    method: "POST",
    url: API_PATHS.globalIndices,
    data: {
      page: page,
      limit: limit ?? 5,
    },
  });
  return response?.data;
};

export const fetchSectorStocks = async (
  sortState: string,
  token?: number,
  page?: number,
  exhId?: number
) => {
  const response = await request<SectorStocksData>({
    method: "POST",
    url: API_PATHS.sectorStocks,
    data: {
      page: page ?? 1,
      limit: 20,
      exchId: exhId ?? 0,
      token: token ?? 26000,
      sort: sortState,
    },
  });
  return response?.data;
};
